// Native-aware location helper. Uses @capacitor/geolocation, which falls back
// to the browser's navigator.geolocation on web automatically.
import { Capacitor } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";
import { toast } from "sonner";

export interface Coords {
  lat: number;
  lng: number;
}

export interface GetLocationOptions {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
}

/**
 * Request the current device position. Returns null on denial / failure and
 * shows a user-facing toast explaining the next step. The caller is expected
 * to have shown their own contextual (soft) explainer UI before invoking
 * this — the function only triggers the native OS permission prompt.
 */
export async function getCurrentCoords(
  opts: GetLocationOptions = {},
): Promise<Coords | null> {
  const { enableHighAccuracy = true, timeout = 10000, maximumAge = 60000 } = opts;

  if (Capacitor.isNativePlatform()) {
    try {
      const status = await Geolocation.checkPermissions();
      if (status.location === "denied") {
        toast.error(
          "Location was previously denied. Enable it in Settings → Like2Fit → Location.",
        );
        return null;
      }
      if (
        status.location === "prompt" ||
        status.location === "prompt-with-rationale"
      ) {
        const requested = await Geolocation.requestPermissions({
          permissions: ["location"],
        });
        if (requested.location !== "granted") {
          toast.info(
            "You can enable location later in Settings if you change your mind.",
          );
          return null;
        }
      }
    } catch (err) {
      console.error("[nativeLocation] permission check failed:", err);
    }
  }

  try {
    const position = await Geolocation.getCurrentPosition({
      enableHighAccuracy,
      timeout,
      maximumAge,
    });
    return {
      lat: position.coords.latitude,
      lng: position.coords.longitude,
    };
  } catch (err) {
    console.error("[nativeLocation] getCurrentPosition failed:", err);
    toast.error("Couldn't get your location. Try again or check permissions.");
    return null;
  }
}
